import { Check } from 'lucide-react';

import { Badge, Button, cn } from '@sandtech-hub-monorepo/ui-kit';

/**
 * Fixed copy, and deliberately so: free access is the absence of a
 * subscription rather than a plan the backend holds, so there is nothing to
 * read for it and nothing to buy. The card describes what a learner already
 * has.
 *
 * "Free modules" here means the modules a program marks as free, which is a
 * per-module split — not a time-limited trial of the paid ones. The
 * subscription is charged in full from the first day.
 */
const FREE_FEATURES = [
  'Get free selected modules in each course for trial',
  'Upgrade anytime for full access',
];

interface Props {
  onStayFree: () => void;
  /** Enrolment in flight; this card started it. */
  busy?: boolean;
  /**
   * Whether Free is actually what the learner has right now. False for a
   * subscriber whose live plan doesn't cover this program yet — the badge
   * would otherwise claim Free for someone already paying.
   */
  isCurrentPlan: boolean;
  className?: string;
}

export const FreePlanCard = ({
  onStayFree,
  busy = false,
  isCurrentPlan,
  className,
}: Props) => (
  <div
    className={cn(
      'flex flex-col rounded-2xl border border-border-secondary bg-card p-8',
      className
    )}
  >
    <h2 className="text-center text-xl font-semibold text-heading">
      Preview
    </h2>
    <p className="mt-4 text-center text-5xl font-bold text-heading">$0</p>
    {isCurrentPlan && (
      <div className="mt-4 flex justify-center">
        <Badge
          variant="outline"
          className="rounded-full border-border px-4 py-1 text-xs font-normal text-muted-foreground"
        >
          Your current plan
        </Badge>
      </div>
    )}
    <p className="mt-4 text-center text-sm text-muted-foreground">
      Sample learning before committing
    </p>
    <ul className="mt-6 flex flex-1 flex-col gap-3">
      {FREE_FEATURES.map((feature) => (
        <li
          key={feature}
          className="flex items-start gap-2 text-sm text-foreground"
        >
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
            <Check className="h-3.5 w-3.5" />
          </span>
          {feature}
        </li>
      ))}
    </ul>
    <Button
      variant="outline"
      disabled={busy}
      onClick={onStayFree}
      className="mt-8 w-full rounded-full border-primary text-primary hover:bg-primary/10 hover:text-primary"
    >
      {busy ? 'Starting…' : 'Stay on Preview'}
    </Button>
  </div>
);
